import React, { memo, useMemo, useState } from "react";
import { capitalize, isUndefined, toLower, get } from 'lodash';
import { useTranslation } from "react-i18next";
import { Dialog } from 'primereact/dialog';

interface IProps {
    item: any,
    titled: any,
    lazyLoading?: any,
    displayLocationName?: any,
}

export function getLocation(item: any) {
    if ( !item ) {
        return '';
    }

    let label: any = '';

    if ( item.address ) {
        label += item.address + ', ';
    }

    if ( item.street_line_1 ) {
        label += item.street_line_1 + ', ';
    }

    if ( item.street_line_2 ) {
        label += item.street_line_2 + ', ';
    }

    if ( item.zip ) {
        label += item.zip + ', ';
    }

    if ( item.city ) {
        label += item.city + ', ';
    }

    if ( item.country ) {
        label += capitalize(item.country);
    }

    return label;
}

const FlagIcon = memo(function FlagIcon(props: { src: string, alt: string }) {
    const { src, alt } = props;

    // Keep it very cheap: no observers, no setState.
    return (
        <img
            src={ src }
            alt={ alt }
            className="float-left m-r-5"
            width={ 14 }
            height={ 14 }
            loading="lazy"
            decoding="async"
            onError={ (e) => {
                // Avoid rerenders: hide broken flags via DOM.
                (e.currentTarget as HTMLImageElement).style.display = 'none';
            } }
        />
    );
});

function LocationDialog(props: any) {
    const { label, item, source, lazyLoading } = props;
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        //
        setTimeout(() => ( document.body.style.overflow = 'unset' ), 500);
    };

    return (
        <span>
            { open && (
                <Dialog
                    header={ <>{ t('location') } { t('detail') }</> }
                    visible={ open }
                    onHide={ handleClose }
                >
                    <div>
                        <div className="row">
                            <div className="col-sm-6 text-capitalize">{ t('address') }({ t('name') }):</div>
                            <div className="col-sm-6 text-right text-lowercase"> { item.address } </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6 text-capitalize">{ t('landmark') }({ t('node') }):</div>
                            <div className="col-sm-6 text-right text-lowercase"> { item.landmark } </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6 text-capitalize">{ t('location name') }:</div>
                            <div className="col-sm-6 text-right text-lowercase"> { item.street_line_1 } </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6 text-capitalize">{ t('street name') }:</div>
                            <div className="col-sm-6 text-right text-lowercase"> { item.street_line_2 } </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6 text-capitalize">{ t('zip') }:</div>
                            <div className="col-sm-6 text-right text-lowercase"> { item.zip } </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6 text-capitalize">{ t('city') }:</div>
                            <div className="col-sm-6 text-right text-capitalize"> { item.city } </div>
                        </div>
                        <div className="row ">
                            <div className="col-sm-6">{ t('country') }:</div>
                            <div className="col-sm-6 text-right text-capitalize"> { item.country } </div>
                        </div>
                    </div>
                </Dialog>
            ) }

            <span
                onClick={ handleClickOpen }
                className={ "cursor-pointer" }
                title={ label ?? 'N/A' }
            >
                {
                    source && (
                        <span style={ { position: "relative", top: "3px" } }>
                            <FlagIcon
                                src={ source }
                                alt={ toLower(item.country || '') }
                            />
                        </span>
                    )
                }
                <span className={ "ps-1 text-capitalize" }>
                    { `${ item.city }` }
                    <span className={ "ps-1" }>
                        {
                            ( props.displayLocationName > 0 && item.street_line_1 )
                                ? `${ item.street_line_1 }`
                                : ""
                        }
                    </span>
                </span>
            </span>
        </span>
    );
}

function Location(props: IProps) {
    const { item, titled, lazyLoading } = props;

    const source = useMemo(() => {
        const countrySort = item?.country_sort;
        return countrySort ? `/images/flags/${ toLower(countrySort) }.png` : null;
    }, [item?.country_sort]);

    const label = useMemo(() => getLocation(item), [item]);

    if ( !item ) {
        return ( <span> </span> );
    }

    return (
        <>
            {
                titled ?
                    <LocationDialog
                        source={ source }
                        item={ item }
                        label={ label }
                        displayLocationName={ props.displayLocationName }
                        lazyLoading={ isUndefined(lazyLoading) ? true : lazyLoading }
                    />
                    :
                    <span>
                {
                    item.label
                        ? item.label
                        : label
                }
              </span>
            }
        </>
    );
}

export default memo(Location, (prev: IProps, next: IProps) => {
    return (
        prev.titled === next.titled
        && prev.lazyLoading === next.lazyLoading
        && prev.displayLocationName === next.displayLocationName
        && get(prev.item, ['id']) === get(next.item, ['id'])
    );
});
